import * as React from 'react';
import {
  AddressAutocompleteInput,
  type AddressAutocompleteInputProps,
  type AddressSuggestion,
  type AddressValue,
} from './address-autocomplete-input';
import { getOrderedCountryCodes } from './country-select';

export type InternationalAddressInputProps = Omit<
  AddressAutocompleteInputProps<AddressValue, AddressSuggestion<AddressValue>>,
  | 'suggestionsProvider'
  | 'detailsResolver'
  | 'fixedCountry'
  | 'defaultCountry'
  | 'autocompleteCountries'
  | 'defaultManual'
  | 'renderRegionField'
  | 'onEdit'
  | 'resolving'
>;

function withoutCountrySpecificFields(value: AddressValue): AddressValue {
  return {
    street: value.street,
    streetNumber: value.streetNumber,
    postalCode: value.postalCode,
    city: value.city,
    country: value.country,
  };
}

/** Controlled manual address fields with the complete country list. */
const InternationalAddressInput = React.forwardRef<
  HTMLInputElement,
  InternationalAddressInputProps
>(({ value, onChange, allowedCountries, locale = 'en', ...props }, ref) => {
  const defaultCountry = React.useMemo(
    () => getOrderedCountryCodes(locale, allowedCountries)[0] ?? 'CH',
    [allowedCountries, locale],
  );
  return (
    <AddressAutocompleteInput
      ref={ref}
      {...props}
      value={value ? withoutCountrySpecificFields(value) : null}
      onChange={(nextValue) =>
        onChange(nextValue ? withoutCountrySpecificFields(nextValue) : null)
      }
      allowedCountries={allowedCountries}
      locale={locale}
      defaultCountry={defaultCountry}
      autocompleteCountries={[]}
    />
  );
});

InternationalAddressInput.displayName = 'InternationalAddressInput';

export { InternationalAddressInput };
